"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Calendar, MapPin, Trophy, Users, Euro, Maximize, Layers, LayoutTemplate, Clock, Image as ImageIcon, Medal, ExternalLink, Handshake } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

// --- Interfaces TS ---
interface PodiumBlockProps {
  logo?: string;
  name?: string;
  bgColor: string;
  borderColor: string;
  isLarge?: boolean;
}
interface MedalIconProps {
  rank: number;
  color: string;
  isLarge?: boolean;
}
interface ResolvedLogos {
  winner?: string;
  second?: string;
  third?: string;
  partner?: string;
}
const WP_URL = "https://settled-gobbler.10web.cloud";
const PLACEHOLDER_LOGO = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647492/img/placeholder-logo.png";
const PodiumBlock: React.FC<PodiumBlockProps> = ({
  logo,
  name,
  bgColor,
  borderColor,
  isLarge = false
}) => <div className={`w-full h-auto min-h-[80px] py-3 rounded-t-md flex flex-col items-center justify-center border-x border-t gap-2 ${bgColor} ${borderColor}`}>
    <img src={logo || PLACEHOLDER_LOGO} alt={name || "Équipe"} className={`${isLarge ? 'w-[60px] h-[60px]' : 'w-[50px] h-[50px]'} object-contain ${!logo ? 'opacity-50 grayscale' : ''}`} loading="lazy" />
    <div className="text-[11px] md:text-[13px] font-bold uppercase text-[#ffcc00] text-center leading-tight px-1 line-clamp-2">
      {name || 'Équipe'}
    </div>
  </div>;
const MedalIcon: React.FC<MedalIconProps> = ({
  rank,
  color,
  isLarge = false
}) => <div className="relative">
    <div className={`rounded-full bg-[#242830] flex items-center justify-center overflow-hidden ${isLarge ? 'size-12 border-2 border-[#ffcc00] shadow-[0_0_15px_rgba(255,204,0,0.2)]' : 'size-10 border border-white/10'}`}>
      {rank === 1 ? <Trophy className={`size-6 ${color}`} /> : <Medal className={`size-5 ${color}`} />}
    </div>
    <div className={`absolute -top-1 -right-1 rounded-full flex items-center justify-center font-bold text-black ${isLarge ? 'size-5 bg-[#ffcc00] text-[10px]' : `size-4 ${rank === 2 ? 'bg-slate-400' : 'bg-amber-700 text-white'} text-[8px]`}`}>
      {rank}
    </div>
  </div>;
function TournamentsGridConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  const [resolvedLogos, setResolvedLogos] = useState<Record<string, ResolvedLogos>>({});
  const archivedPosts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return posts.map(post => {
      const fields = post.customFields || {};
      return {
        ...post,
        tournamentName: fields.name || post.title,
        tournamentDate: fields.date ? new Date(fields.date) : null,
        tournamentLocation: fields.location || "Lieu non précisé"
      };
    }).filter(post => {
      if (!post.tournamentDate) return false;
      const compareDate = new Date(post.tournamentDate);
      compareDate.setHours(0, 0, 0, 0);
      return compareDate.getTime() < today.getTime();
    }).sort((a, b) => (b.tournamentDate?.getTime() || 0) - (a.tournamentDate?.getTime() || 0));
  }, [posts]);
  const resolveImage = useCallback(async (val: unknown): Promise<string | null> => {
    if (!val) return null;
    if (typeof val === 'string' && val.startsWith('http')) return val;
    if (typeof val === 'number' || typeof val === 'string' && /^\d+$/.test(val)) {
      try {
        const response = await fetch(`${WP_URL}/wp-json/wp/v2/media/${val}`);
        if (!response.ok) return null;
        const data = await response.json();
        return data.source_url || null;
      } catch {
        return null;
      }
    }
    return null;
  }, []);
  useEffect(() => {
    const resolveAllLogos = async () => {
      const pendingPosts = archivedPosts.filter(p => !resolvedLogos[p.id]);
      if (pendingPosts.length === 0) return;
      const results = await Promise.all(pendingPosts.map(async post => {
        const fields = post.customFields || {};
        const [winner, second, third, partner] = await Promise.all([resolveImage(fields.winner_logo), resolveImage(fields.second_place_logo), resolveImage(fields.third_place_logo), resolveImage(fields.partner_club_logo)]);
        return {
          id: post.id,
          logos: {
            winner: winner || undefined,
            second: second || undefined,
            third: third || undefined,
            partner: partner || undefined
          }
        };
      }));
      setResolvedLogos(prev => {
        const updated = {
          ...prev
        };
        results.forEach(res => {
          updated[res.id] = res.logos;
        });
        return updated;
      });
    };
    resolveAllLogos();
  }, [archivedPosts, resolveImage]);
  if (loading && posts.length === 0) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} className="h-[450px] animate-pulse rounded-lg border-none bg-[#1a1d24]/50" />)}
      </div>;
  }
  if (error || archivedPosts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-24 text-center">
        <Trophy className="mb-4 size-12 text-muted-foreground/50" />
        <p className="text-lg text-muted-foreground">Aucun tournoi terminé trouvé dans les archives.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-3">
        {archivedPosts.map((post, i) => {
        const postFields = post.customFields || {};
        const displayDate = post.tournamentDate ? post.tournamentDate.toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }) : "Date passée";
        const logos = resolvedLogos[post.id] || {};
        return <div key={post.id} className="h-full" data-index={i}>
              <Card className="group h-full flex flex-col overflow-hidden border-none bg-[#1a1d24] shadow-lg transition-all duration-300 hover:-translate-y-2 rounded-lg text-white py-0">
                <CardHeader className="p-0 relative">
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    <Badge className="bg-gray-600 text-white uppercase tracking-wider border-none px-3 py-1 text-[10px]">
                      Terminé
                    </Badge>
                    {postFields.tournament_type && <Badge className="bg-blue-700 text-white uppercase tracking-wider border-none px-3 py-1 text-[10px]">
                        {postFields.tournament_type}
                      </Badge>}
                    {postFields.has_pro_teams === "1" && <Badge className="bg-amber-600 text-white uppercase tracking-wider border-none px-3 py-1 text-[10px]">
                        Élite
                      </Badge>}
                  </div>
                </CardHeader>

                <CardContent className="flex-grow px-4 pt-5 flex flex-col gap-3">
                  <CardTitle className="text-2xl font-bold text-[#f1f3f5] uppercase tracking-tight leading-tight">
                    {post.tournamentName}
                  </CardTitle>

                  <div className="flex items-center gap-2 text-xs text-[#ffcc00] font-medium uppercase tracking-wider">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    <span>{displayDate}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-[#a0aec0] font-medium">
                    <MapPin className="size-3.5" />
                    <span className="line-clamp-1">{post.tournamentLocation}</span>
                  </div>

                  <div className="flex flex-col gap-2 py-2">
                    <div className="flex flex-col gap-1.5 mt-1">
                      {postFields.wvc_tournoi_age_categories && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Users className="size-3.5 text-[#ffcc00]/70" />
                          <div className="font-medium">Catégorie(s) :</div>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_age_categories}
                          </Badge>
                        </div>}
                      {postFields.wvc_tournoi_game_format && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <LayoutTemplate className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Format :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_game_format}
                          </Badge>
                        </div>}
                      {postFields.max_players && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Users className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Joueurs :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.max_players}
                          </Badge>
                        </div>}
                      {postFields.wvc_tournoi_match_duration && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Clock className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Durée :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_match_duration}
                          </Badge>
                        </div>}
                      {postFields.wvc_tournoi_pitch_type && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Layers className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Terrain :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_pitch_type}
                          </Badge>
                        </div>}
                      {postFields.matchs_garantis && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Trophy className="size-3.5 text-[#ffcc00]/70" />
                          <div className="font-medium">Matchs garantis :</div>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.matchs_garantis}
                          </Badge>
                        </div>}
                      {postFields.wvc_tournoi_max_teams && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Maximize className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Nombre d'équipes :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_max_teams}
                          </Badge>
                        </div>}
                      {postFields.partner_club_name && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Handshake className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Club partenaire :</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                              {postFields.partner_club_name}
                            </Badge>
                            <img src={logos.partner || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647492/img/placeholder-logo.png"} alt={postFields.partner_club_name} className="w-[25px] h-[25px] object-contain" loading="lazy" />
                          </div>
                        </div>}
                      {postFields.gallery_url && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <ImageIcon className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Galerie :</span>
                          <Link to={postFields.gallery_url} newTab className="text-[#ffcc00] hover:underline">
                            Voir les photos
                          </Link>
                        </div>}
                      {postFields.wvc_tournoi_registration_fee && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Euro className="size-3.5 text-[#ffcc00]/70" />
                          <div className="font-medium">Frais d'inscription :</div>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_registration_fee}
                          </Badge>
                        </div>} 
                      {postFields.lien_vers_resultats && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <ExternalLink className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Résultats :</span>
                          <Link to={postFields.lien_vers_resultats} newTab className="text-[#ffcc00] hover:underline">
                            Consulter
                          </Link>
                        </div>}
                    </div>
                  </div>

                  {/* Podium */}
                  {(postFields.winner_name || postFields.second_place_name || postFields.third_place_name) && <div className="mt-6 space-y-4">
                      <div className="flex items-center gap-2 text-xs text-[#ffcc00] font-bold uppercase tracking-widest">
                        <Trophy className="size-4" />
                        <span>Podium Final</span>
                      </div>
                      
                      <div className="flex items-end justify-between gap-2 pb-2 px-2">
                        <div className="flex flex-col items-center flex-1 gap-2">
                          <MedalIcon rank={2} color="text-slate-400" />
                          <PodiumBlock logo={logos.second} name={postFields.second_place_name} bgColor="bg-slate-400/20" borderColor="border-[#ffcc00]/30" />
                        </div>

                        <div className="flex flex-col items-center flex-1 gap-2">
                          <MedalIcon rank={1} color="text-[#ffcc00]" isLarge />
                          <PodiumBlock logo={logos.winner} name={postFields.winner_name} bgColor="bg-[#ffcc00]/20" borderColor="border-[#ffcc00]/30" isLarge />
                        </div>

                        <div className="flex flex-col items-center flex-1 gap-2">
                          <MedalIcon rank={3} color="text-amber-700" />
                          <PodiumBlock logo={logos.third} name={postFields.third_place_name} bgColor="bg-amber-700/20" borderColor="border-amber-700/30" />
                        </div>
                      </div>
                    </div>}
                </CardContent>
              </Card>
            </div>;
      })}
      </div>

      {hasNext && <div className="flex justify-center pt-8">
          <Button variant="outline" onClick={() => loadMore()} disabled={isRefetching} className="rounded-lg border-2 border-[#ffcc00] bg-transparent px-8 py-6 font-bold uppercase tracking-wider text-[#ffcc00] hover:bg-[#ffcc00] hover:text-[#121417]">
            {isRefetching ? "Chargement..." : "Charger plus d'archives"}
          </Button>
        </div>}
    </div>;
}
export default function TournamentsArchive() {
  return <section id="tournaments-archive" className="bg-background text-foreground py-12">
      {/* <div className="container mx-auto px-4 md:px-6"> */}
      <div className="max-w-[1600px] mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tighter uppercase">
            Archives des Tournois
          </h2>
          <div className="mt-4 h-1 w-24 bg-[#ffcc00]" />
        </div>

        <PostsProvider wp_query={{
        post_type: "tournoi",
        per_page: -1,
        order: "desc",
        orderby: "date"
      }}>
          <TournamentsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}